import { FaEraser, FaSearch } from "react-icons/fa";
import Button from "../../../../components/button/button";
import { Card } from "../../../../components/card/card";
import InputForm from "../../../../components/input/input-form";
import { Template } from "../../../../components/template/template";
import { AgGridReact } from "ag-grid-react";
import InputDateTime from "../../../../components/input/input-datetime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import AgGridUtils from "../../../../utils/aggrid-utils";
import ProjectUtils from "../../../../utils/project-utils";
import NotificationsService from "../../../../services/web/notification-service";
import NotificationDetails from "../components/notification-details";
import InputDatetime from "../../../../components/input/input-datetime";

export default function NotificationSigned() {
    const initialFilter = { title: "", dateStart: null, dateEnd: null };

    const [filters, setFilters] = useState(initialFilter);
    const [selected, setSelected] = useState(new Map());
    const [config, setConfig] = useState(null);
    const [apply, setApply] = useState(0);
    const gridRef = useRef(null);

    const { data: list = [], isLoading } = useQuery({
        queryKey: ["notificationSignedList", apply],
        queryFn: async () => {
            const payload = {
                filter: [
                    { field: "title", type: "like", value: filters.title },
                    {
                        field: "dateBetween",
                        type: "between",
                        value: {
                            dateStart: filters.dateStart ? new Date(filters.dateStart).toISOString() : null,
                            dateEnd: filters.dateEnd ? new Date(filters.dateEnd).toISOString() : null,
                        },
                    },
                ],
                page: 1, size: 0, sort: [{ field: "dt_event", dir: "desc" }],
            };

            const data = await NotificationsService.signedList(payload);
            return Array.isArray(data) ? data : (data?.data ?? []);
        },
    });

    const first = useRef(true);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (!list?.length) return;

        if (first.current && searchParams.has("id")) {
            const idParam = Number(searchParams.get("id"));
            const found = list.find((e) => Number(e.id) === idParam);

            if (found) {
                setSelected(new Map([[found.id, found]]));

                gridRef.current?.api?.forEachNode((node) => { node.setSelected(Number(node.data?.id) === Number(found.id)); });
            };

            first.current = false;
            return;
        };

        const currentSelected = selected?.size === 1 ? selected.values().next().value : null;
        if (!currentSelected) return;
        
        const stillExists = list.some((item) => Number(item.id) === Number(currentSelected.id));

        gridRef.current?.api?.forEachNode((node) => { node.setSelected(Number(node.data?.id) === Number(currentSelected.id)); });
    }, [list]);

    useEffect(() => {
        let alive = true;

        (async () => {
            const el = document.getElementById("root");
            const mapsKeyFromDom = (el?.dataset?.mapsKey ?? "").trim();

            try {
            const data = await NotificationsService.getConfig();
            if (!alive) return;

            setConfig({
                mapsKey: mapsKeyFromDom || (data?.mapsKey ?? ""),
                hasDispatch: !!data?.hasDispatch,
            });
            } catch (err) {
            console.error(err);
            if (!alive) return;

            setConfig({
                mapsKey: mapsKeyFromDom,
                hasDispatch: false,
            });
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    const columnDefs = useMemo(() => [
        { 
            headerName: "D/H", 
            headerTooltip: "Data/hora" , 
            field: "dtEvent", 
            sort: "desc" ,
            flex: 1,
            tooltipValueGetter: (params) =>
                params.value
                    ? ProjectUtils.formatDate(params.value, "DD/MM/YYYY hh:mm:ss")
                    : "", 
            valueFormatter: (params) => 
                params.value
                    ? ProjectUtils.formatDate(params.value, "DD/MM/YYYY hh:mm:ss")
                    : "",
            cellStyle: ({ data }) => data?.delayed ? { color: "#ff5a5a" } : null,
         },
        { headerName: "Notificação", headerTooltip: "Notificação" , field: "title", tooltipField: "title" ,flex: 1, }, 
    ], []);

    const defaultColDef = useMemo(() => ({
        resizable: false,
        sortable: true,
    }), []);

    const rowSelection = useMemo(() => ({
        mode: "singleRow",
        checkboxes: false,
    }), []);

    const onCellClicked = ({ data, node }) => {
        setSelected((prev) => {
            const next = new Map();
            const alreadySelected = prev.size === 1 && prev.values().next().value?.id === data.id;

            if (!alreadySelected) {
                next.set(data.id, data);
                node.setSelected(true);
            } else {
                node.setSelected(false);
            }
            return next;
        });
    };

    const handleTitle = (e) => setFilters(p => ({...p, title: e.target.value}));

    const handleDate = (key, value) => setFilters(p => ({...p, [key]: value }));

    const handleClear = () => {
        setFilters(initialFilter);
        setSelected(new Map());
        gridRef.current?.api?.deselectAll();
        setApply(v => v + 1);
    };

    const handleSearch = () => {
        if (filters.dateStart && !filters.dateEnd) {
            ProjectUtils.swalErrorMessage("", `O campo "Data/hora final" é obrigatório.`, "Ok");
            return;
        };

        if (filters.dateEnd && !filters.dateStart) {
            ProjectUtils.swalErrorMessage("", `O campo "Data/hora inicial" é obrigatório.`, "Ok");
            return;
        };

        if (filters.dateStart && filters.dateEnd) {
            const start = new Date(filters.dateStart).getTime();
            const end = new Date(filters.dateEnd).getTime();

            if (start > end) {
                ProjectUtils.swalErrorMessage("", "A data inicial não pode ser maior que a data final.", "Ok");
                return;
            };
        };

        setApply((v) => v + 1);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") { e.preventDefault(); handleSearch(); }
        if (e.key === "Escape") { e.preventDefault(); handleClear(); }
    }

    const returnToDefault = () => {
        setSelected(new Map());
        gridRef.current?.api.deselectAll();
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Notificações assinadas</Template.Title>
            </Template.Header>
            <Template.Body>
                <div className="grid grid-cols-12 gap-2 items-stretch h-[calc(100vh-69px)]">
                    <div className="col-span-4 flex flex-col gap-2 h-full min-h-0">
                        <Card className={"flex-none"}>
                            <Card.Body className={"mt-1"}>

                                <div className={"flex items-stretch"}>
                                    <InputDatetime
                                        id="dateStart"
                                        label="Data/Hora entre"
                                        selected={filters.dateStart}
                                        onChange={(v) => handleDate("dateStart", v)}
                                        onKeyDown={onKeyDown}
                                        dateFormat="LL"
                                        customIcon
                                        inputClass="!rounded-e-none"
                                        classNameWrapper="w-[47%]"
                                    />

                                    <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                    <InputDatetime
                                        id="dtEnd"
                                        selected={filters.dateEnd}
                                        onChange={(v) => handleDate("dateEnd", v)}
                                        onKeyDown={onKeyDown}
                                        dateFormat="LL"
                                        inputClass="!rounded-s-none"
                                        classNameWrapper="w-[47%]"
                                    />
                                </div>

                                <div className="mt-3 flex gap-4 items-end">
                                    <div className="flex-1">
                                        <InputForm
                                            id="title"
                                            label="Notificação"
                                            value={filters.title}
                                            onChange={handleTitle}
                                            onKeyDown={onKeyDown}
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            target="text-white"
                                            title="Limpar"
                                            onClick={handleClear}
                                        >
                                            <FaEraser />
                                        </Button>
                                        <Button 
                                            title="Buscar"
                                            onClick={handleSearch}
                                        >
                                            <FaSearch />
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className={"flex-1 min-h-0"}>
                            <Card.Body className={"h-full min-h-0"}>
                                <div className="h-full min-h-0">
                                    <div className="h-full min-h-0 aggrid-row-pointer grid-users">
                                        <AgGridReact 
                                            ref={gridRef}
                                            rowData={list}
                                            columnDefs={columnDefs}
                                            defaultColDef={defaultColDef}
                                            rowSelection={rowSelection}
                                            suppressCellFocus={true}
                                            onCellClicked={onCellClicked}
                                            animateRows={false}
                                            rowHeight={32}
                                            pagination
                                            paginationAutoPageSize={true}
                                            gridOptions={{localeText: AgGridUtils.localization()}}
                                            theme={AgGridUtils.generalTheme()}
                                            enableBrowserTooltips={true}
                                        />
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-span-8 flex min-w-0 h-full min-h-0">
                        <Card className={"w-full h-full"}>
                            <Card.Body className={"h-full p-0"}>
                                <div className="h-full relative">
                                    <div 
                                        hidden={ selected?.size === 1 }
                                        className="h-full flex items-center justify-center p-4 select-none pointer-events-none opacity-50"
                                    >
                                        <img 
                                            src={NotificationsService.corporationImage}
                                            className="max-h-full max-w-full object-contain"
                                        />
                                    </div>
                                    {selected?.size === 1 && (
                                        <div className="h-full p-4">
                                            <NotificationDetails 
                                                selected={selected.values().next().value}
                                                mapsKey={config.mapsKey}
                                                returnToDefault={returnToDefault}
                                                target={"signed"}
                                                hasDispatch={config.hasDispatch}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </Template.Body>
        </Template>
    )
}